import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Stack,
    Grid,
    Paper,
    Divider,
    Alert,
    Chip
} from '@mui/material';
import { useNotify } from 'react-admin';
import restProvider from '../../rest.provider';

// Local printer storage service (same as in Settings)
const LocalPrinterStorage = {
    STORAGE_KEY_SELECTIONS: 'zWarehouse_printer_selections',
    
    getPrinterSelections() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY_SELECTIONS);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error loading printer selections:', error);
            return {};
        }
    }
};

const CustomLabelsScreen = () => {
    const notify = useNotify();
    const [labelData, setLabelData] = useState({
        studentName: '',
        numLabels: 1,
        labelType: 'student_labels'
    });
    const [previewData, setPreviewData] = useState(null);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPrinter, setIsLoadingPrinter] = useState(true);

    const loadSelectedPrinter = useCallback(async () => {
        setIsLoadingPrinter(true);
        try {
            // Get the printer selected for student labels from settings
            const printerSelections = LocalPrinterStorage.getPrinterSelections();
            const studentPrinter = printerSelections.student;
            
            if (studentPrinter) {
                console.log('Found selected student printer:', studentPrinter);
                setSelectedPrinter(studentPrinter);
            } else {
                console.log('No student printer selected in settings');
                setSelectedPrinter(null);
            }
        } catch (error) {
            console.error('Failed to load selected printer:', error);
            setSelectedPrinter(null);
        } finally {
            setIsLoadingPrinter(false);
        }
    }, []);

    useEffect(() => {
        loadSelectedPrinter();
    }, [loadSelectedPrinter]);

    // Auto-update preview when label data changes
    useEffect(() => {
        if (labelData.studentName.trim()) {
            const preview = {
                studentName: labelData.studentName,
                numLabels: labelData.numLabels,
                // Add ZPL for debugging
                zpl: generateStudentLabelZpl(labelData.studentName)
            };
            setPreviewData(preview);
        } else {
            setPreviewData(null);
        }
    }, [labelData]);

    const handlePrint = async () => {
        if (!labelData.studentName.trim()) {
            notify('Please enter a student name', { type: 'warning' });
            return;
        }

        if (!selectedPrinter) {
            notify('No printer selected. Please go to Settings to select a student labels printer.', { type: 'warning' });
            return;
        }
        
        console.log('Selected printer:', selectedPrinter);
        console.log('Label data:', labelData);

        setIsLoading(true);
        try {
            // Generate ZPL for the label
            const zpl = generateStudentLabelZpl(labelData.studentName);
            console.log('Generated ZPL:', zpl);

            // Send print request to the API using the selected printer's IP
            const result = await restProvider.printStudentLabel(
                selectedPrinter.ip,
                labelData.studentName,
                labelData.numLabels,
                zpl
            );

            if (result.data.success) {
                notify(`Successfully printed ${labelData.numLabels} label(s) to ${selectedPrinter.name || selectedPrinter.ip}`, { type: 'success' });
                // Clear the form after successful print
                setLabelData({
                    studentName: '',
                    numLabels: 1,
                    labelType: 'student_labels'
                });
                setPreviewData(null);
            } else {
                notify(`Print failed: ${result.data.error || 'Unknown error'}`, { type: 'error' });
            }
        } catch (error) {
            console.error('Print error:', error);
            notify(`Print failed: ${error.message}`, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const generateStudentLabelZpl = (studentName) => {
        // Generate ZPL using the same format as the API
        // Based on API response: ^XA^MMT^MTT^LH80,60^FB520,1,0,C^A0N,56,56^FDTest Student^FS^XZ
        return `^XA^MMT^MTT^LH80,60^FB520,1,0,C^A0N,56,56^FD${studentName}^FS^XZ`;
    };

    const handleInputChange = (field, value) => {
        setLabelData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const LabelPreview = ({ data }) => {
        if (!data) return null;

        const { studentName, numLabels } = data;
        
        return (
            <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" gutterBottom>Preview</Typography>
                <Grid container spacing={2}>
                    {Array.from(Array(numLabels).keys()).map((_, index) => (
                        <Grid item xs={6} key={index}>
                            <Box
                                sx={{
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    p: 1.5,
                                    textAlign: 'center',
                                    minHeight: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    aspectRatio: '4/1', // More realistic label aspect ratio
                                    maxWidth: '100%'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: '18px', // Fixed font size for preview
                                        fontWeight: 'bold',
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {studentName}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {numLabels} label(s) will be printed
                </Typography>
            </Paper>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Custom Student Labels
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Design, preview, and print custom student labels
            </Typography>

            <Grid container spacing={3}>
                {/* Design Panel */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Label Design
                            </Typography>
                            
                            <Stack spacing={3}>
                                <TextField
                                    label="Student Name"
                                    value={labelData.studentName}
                                    onChange={(e) => handleInputChange('studentName', e.target.value)}
                                    fullWidth
                                    required
                                    placeholder="Enter student name"
                                />

                                <TextField
                                    label="Number of Labels"
                                    type="number"
                                    value={labelData.numLabels}
                                    onChange={(e) => handleInputChange('numLabels', parseInt(e.target.value) || 1)}
                                    fullWidth
                                    required
                                    inputProps={{ min: 1, max: 50 }}
                                    helperText="Enter number of labels to print (1-50)"
                                />

                                <Divider />

                                {/* Selected Printer Display */}
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Selected Printer
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={loadSelectedPrinter}
                                            disabled={isLoadingPrinter}
                                        >
                                            Refresh
                                        </Button>
                                    </Stack>
                                    
                                    {isLoadingPrinter ? (
                                        <Alert severity="info">
                                            Loading printer information...
                                        </Alert>
                                    ) : selectedPrinter ? (
                                        <Box sx={{ 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: 1, 
                                            p: 2, 
                                            backgroundColor: '#f8f9fa' 
                                        }}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Chip 
                                                    label="Student Labels Printer" 
                                                    color="primary" 
                                                    size="small" 
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {selectedPrinter.name || selectedPrinter.ip}
                                                    </Typography>
                                                    {selectedPrinter.location && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {selectedPrinter.location}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </Box>
                                    ) : (
                                        <Alert severity="warning">
                                            No student labels printer selected. Please go to Settings to select a printer for student labels.
                                        </Alert>
                                    )}
                                </Box>

                                <Button
                                    variant="contained"
                                    onClick={handlePrint}
                                    disabled={isLoading || isLoadingPrinter || !labelData.studentName.trim() || !selectedPrinter}
                                    fullWidth
                                    size="large"
                                >
                                    Print Labels
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Preview Panel */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Label Preview
                            </Typography>
                            
                            {previewData ? (
                                <LabelPreview data={previewData} />
                            ) : (
                                <Box
                                    sx={{
                                        border: '2px dashed #ccc',
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: 'center',
                                        minHeight: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography color="text.secondary">
                                        Enter a student name to see the label preview
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export { CustomLabelsScreen }; 